// Render-time Cloudinary URL transforms. Stored URLs are rendered verbatim
// today, so a 2400x2400 master gets served even for a 400px thumbnail slot.
// This helper injects f_auto,q_auto,w_<display>,dpr_auto into the path so the
// CDN negotiates format + resolution per client.

const CLOUDINARY_REGEX = /^https:\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\/(.+)$/;

// Segments that look like a transformation block (e.g., "w_800,f_auto,q_auto")
const TRANSFORM_BLOCK = /^[a-z]_[^,/]+(?:,[a-z]_[^,/]+)*$/;

export type CloudinaryOptions = {
  width?: number;
  quality?: "auto" | number;
  format?: "auto" | "webp" | "jpg" | "avif";
  dpr?: "auto" | number;
  /** Crop to this width/height ratio around the detected subject (g_auto)
   *  at the source's native resolution, before resizing. */
  fillAspect?: number;
};

export function buildCloudinaryUrl(url: string | null | undefined, opts: CloudinaryOptions = {}): string {
  if (!url) return "";
  const match = url.match(CLOUDINARY_REGEX);
  if (!match) return url;

  const [, cloud, rest] = match;

  const transforms = [
    `f_${opts.format ?? "auto"}`,
    `q_${opts.quality ?? "auto"}`,
    opts.width ? `w_${opts.width}` : null,
    // c_limit: shrink to fit but never enlarge. Without it Cloudinary upscales
    // a 1280px master to w_1600 and re-encodes the blur — pixelated banners.
    opts.width ? "c_limit" : null,
    `dpr_${opts.dpr ?? "auto"}`,
  ]
    .filter(Boolean)
    .join(",");
  // Chained so the subject-aware crop runs at native resolution and the
  // c_limit resize afterwards can only shrink it.
  const fill = opts.fillAspect ? `c_fill,g_auto,ar_${opts.fillAspect}/` : "";

  // Split the rest at the first segment. If the first segment already is a
  // transform block, merge our transforms into it (Cloudinary uses the first
  // transform block; anything else is treated as folder path). Simpler and
  // safer: always prepend our block before the existing path.
  const firstSegment = rest.split("/")[0];
  if (TRANSFORM_BLOCK.test(firstSegment)) {
    // Merge: put ours first, then the existing transform block, then the rest
    const tail = rest.split("/").slice(1).join("/");
    return `https://res.cloudinary.com/${cloud}/image/upload/${fill}${transforms}/${firstSegment}/${tail}`;
  }
  return `https://res.cloudinary.com/${cloud}/image/upload/${fill}${transforms}/${rest}`;
}

export function buildSrcSet(url: string | null | undefined, widths: number[] = [400, 800, 1200, 1600]): string {
  if (!url) return "";
  return widths
    .map((w) => `${buildCloudinaryUrl(url, { width: w, dpr: 1 })} ${w}w`)
    .join(", ");
}

/** URL of Cloudinary's JSON metadata for an image: original size, plus the
 *  subject-aware (g_auto) crop it would take at `fillAspect`. CORS-enabled. */
export function buildCloudinaryInfoUrl(url: string, fillAspect?: number): string | null {
  const match = url.match(CLOUDINARY_REGEX);
  if (!match) return null;
  const [, cloud, rest] = match;
  const crop = fillAspect ? `c_fill,g_auto,ar_${fillAspect}/` : "";
  return `https://res.cloudinary.com/${cloud}/image/upload/${crop}fl_getinfo/${rest}`;
}
